/**
 * One glide engine for every sliding nav selection card (desktop rail, tablet
 * dock, phone bar) so all three feel identical. Pure maths + a tiny rAF driver
 * that writes straight to the DOM through `onFrame` (no React render per frame).
 *
 * The ease is a cubic ease-out plus a small settle bump whose size is capped in
 * PIXELS (not a ratio), so a long glide never overshoots visibly further than a
 * short one.
 */

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const easeOutCubic = (t) => 1 - (1 - t) ** 3;

/** 180ms + 0.3ms per px, clamped to [min, max]. */
export function glideDurationMs(distancePx, { min = 180, max = 300 } = {}) {
  return clamp(180 + 0.3 * Math.abs(distancePx), min, max);
}

/** Progress multiplier for t in [0,1]: exactly 0 at 0 and exactly 1 at 1. */
export function glideEase(t, distancePx, maxOvershootPx = 3) {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  const d = Math.abs(distancePx);
  const base = easeOutCubic(t);
  const amp = Math.min(maxOvershootPx, 0.06 * d) / Math.max(d, 1);
  return base + amp * Math.sin(Math.PI * clamp((t - 0.55) / 0.45, 0, 1));
}

/** Position at t, clamped to `bounds` ([min, max]) when given. */
export function glidePosition(from, to, t, { bounds, maxOvershootPx = 3 } = {}) {
  const pos = from + (to - from) * glideEase(t, to - from, maxOvershootPx);
  if (!bounds) return pos;
  const lo = Math.min(bounds[0], bounds[1]);
  const hi = Math.max(bounds[0], bounds[1]);
  return clamp(pos, lo, hi);
}

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/**
 * Drive one glide. `from` / `getTarget()` return `{ pos, size? }`; the target is
 * re-read EVERY frame so a target that is itself moving (the phone chip
 * morphing wider) is tracked exactly. `onDone` fires after the final frame
 * (never after `cancel()`). `bounds` may be an array or a function
 * returning one. Always finishes with `onFrame(target)`. Returns `cancel()`.
 */
export function runGlide({
  from, getTarget, durationMs, reducedMotion = false, onFrame, onDone, bounds, maxOvershootPx = 3, stretch = 0,
}) {
  if (reducedMotion || typeof requestAnimationFrame !== 'function') {
    onFrame(getTarget());
    if (onDone) onDone();
    return () => {};
  }
  let raf = null;
  let cancelled = false;
  const start = now();
  const initial = getTarget();
  const sized = from.size != null && initial.size != null;
  const step = () => {
    raf = null;
    if (cancelled) return;
    const t = clamp((now() - start) / durationMs, 0, 1);
    const target = getTarget();
    if (t >= 1) { onFrame(target); if (onDone) onDone(); return; }
    const b = typeof bounds === 'function' ? bounds() : bounds;
    const frame = { pos: glidePosition(from.pos, target.pos, t, { bounds: b, maxOvershootPx }) };
    if (sized && target.size != null) {
      frame.size = from.size + (target.size - from.size) * easeOutCubic(t);
    }
    // Liquid stretch (opt-in, x-axis cards): peak `stretch` at mid-travel, 1 at both ends.
    if (stretch > 1) frame.stretch = 1 + (stretch - 1) * Math.sin(Math.PI * t);
    onFrame(frame);
    raf = requestAnimationFrame(step);
  };
  raf = requestAnimationFrame(step);
  return () => {
    cancelled = true;
    if (raf != null && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(raf);
    raf = null;
  };
}
